import { useState } from 'react';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import {
    MACHINERY_TYPE_OPTIONS,
    LEGAL_REGIME_OPTIONS,
    COST_TYPE_OPTIONS,
    MAX_OWNERS,
} from '../../../../types/Machinery';
import type {
    MachineryType as MachineryTypeValue,
    LegalRegime as LegalRegimeValue,
    MachineryOwner,
    MachineryCost,
} from '../../../../types/Machinery';
import type { ErrorField } from '../../../../types/ErrorField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';

const UF_OPTIONS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']
    .map(uf => ({ value: uf, label: uf }));

interface MachineryFormProps {
    isCreating: boolean;
    errorFields: ErrorField[];
    name: string; setName: (v: string) => void;
    description: string; setDescription: (v: string) => void;
    machineryType: MachineryTypeValue | ''; setMachineryType: (v: MachineryTypeValue | '') => void;
    legalRegime: LegalRegimeValue | ''; setLegalRegime: (v: LegalRegimeValue | '') => void;
    invoiceValue: number | ''; setInvoiceValue: (v: number | '') => void;
    custodyAddress: string; setCustodyAddress: (v: string) => void;
    custodyCity: string; setCustodyCity: (v: string) => void;
    custodyState: string; setCustodyState: (v: string) => void;
    rtdRegistryOffice: string; setRtdRegistryOffice: (v: string) => void;
    registryOffice: string; setRegistryOffice: (v: string) => void;
    registryState: string; setRegistryState: (v: string) => void;
    registrationNumber: string; setRegistrationNumber: (v: string) => void;
    brand: string; setBrand: (v: string) => void;
    model: string; setModel: (v: string) => void;
    yearManufacture: number | ''; setYearManufacture: (v: number | '') => void;
    yearModel: number | ''; setYearModel: (v: number | '') => void;
    serialNumber: string; setSerialNumber: (v: string) => void;
    finameCode: string; setFinameCode: (v: string) => void;
    color: string; setColor: (v: string) => void;
    comments: string; setComments: (v: string) => void;
    creditOperationId: string; setCreditOperationId: (v: string) => void;
    obligations: any[]; isLoadingObligations: boolean;
    owners: MachineryOwner[];
    addOwner: (o: MachineryOwner) => void;
    removeOwner: (i: number) => void;
    ownerSearch: string; setOwnerSearch: (v: string) => void;
    ownerResults: any[]; isSearchingOwner: boolean; showOwnerDropdown: boolean;
    searchOwner: (query: string) => void; handleSelectOwner: (person: any) => void;
    costs: MachineryCost[]; addCost: (c: MachineryCost) => void; removeCost: (i: number) => void;
}


const MachineryForm = ({
    isCreating, errorFields,
    name, setName,
    description, setDescription,
    machineryType, setMachineryType,
    legalRegime, setLegalRegime,
    invoiceValue, setInvoiceValue,
    custodyAddress, setCustodyAddress,
    custodyCity, setCustodyCity,
    custodyState, setCustodyState,
    rtdRegistryOffice, setRtdRegistryOffice,
    registryOffice, setRegistryOffice,
    registryState, setRegistryState,
    registrationNumber, setRegistrationNumber,
    brand, setBrand,
    model, setModel,
    yearManufacture, setYearManufacture,
    yearModel, setYearModel,
    serialNumber, setSerialNumber,
    finameCode, setFinameCode,
    color, setColor,
    comments, setComments,
    creditOperationId, setCreditOperationId,
    obligations, isLoadingObligations,
    owners, addOwner, removeOwner,
    ownerSearch, setOwnerSearch,
    ownerResults, isSearchingOwner, showOwnerDropdown,
    searchOwner, handleSelectOwner,
    costs, addCost, removeCost,
}: MachineryFormProps) => {
    const [showCostForm, setShowCostForm] = useState(false);
    const [costType, setCostType] = useState('');
    const [costValue, setCostValue] = useState(0);
    const [costDate, setCostDate] = useState('');
    const [costRegNumber, setCostRegNumber] = useState('');
    const [costRegDate, setCostRegDate] = useState('');

    const handleAddCost = () => {
        if (!costType || !costValue || !costDate) return;
        addCost({
            costType: costType as any,
            value: costValue,
            costDate,
            registrationNumber: costRegNumber || undefined,
            registrationDate: costRegDate || undefined,
        });
        setCostType(''); setCostValue(0); setCostDate('');
        setCostRegNumber(''); setCostRegDate('');
        setShowCostForm(false);
    };

    return (
        <div className="p-4 space-y-6">
            {/* Obrigação (opcional) */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Obrigação</h4>
                <SelectTextField label="Selecione a obrigação" value={creditOperationId}
                    onChange={e => setCreditOperationId(e.target.value)}
                    items={obligations.map((o: any) => ({
                        value: o.creditOperationId,
                        label: `${o.operationNumber} — ${o.assignorName || o.operationCode || ''}`,
                    }))}
                    emptyLabel={isLoadingObligations ? 'Carregando...' : 'Selecione'}
                    errorMessage={getErrorMessage(errorFields, 'creditOperationId')} />
            </section>

            {/* Dados do Maquinário */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Dados do Maquinário</h4>
                <div className="grid grid-cols-3 gap-3">
                    <SelectTextField label="Tipo" value={machineryType}
                        onChange={(e) => setMachineryType(e.target.value as MachineryTypeValue | '')}
                        items={MACHINERY_TYPE_OPTIONS}
                        emptyLabel="Selecione o tipo (opcional)"
                        errorMessage={getErrorMessage(errorFields, 'machineryType')} />
                    <TextEdit label="Nome / Identificação" value={name}
                        onChange={e => setName(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'name')} />
                    <SelectTextField label="Regime Jurídico" value={legalRegime}
                        onChange={(e) => setLegalRegime(e.target.value as LegalRegimeValue | '')}
                        items={LEGAL_REGIME_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'legalRegime')} />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="Marca" value={brand}
                        onChange={e => setBrand(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'brand')} />
                    <TextEdit label="Modelo" value={model}
                        onChange={e => setModel(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'model')} />
                    <TextEdit label="Cor" value={color}
                        onChange={e => setColor(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'color')} />
                </div>

                <div className="grid grid-cols-4 gap-3 mt-3">
                    <TextEdit label="Ano de Fabricação" type="number" value={yearManufacture}
                        onChange={e => setYearManufacture(e.target.value === '' ? '' : Number(e.target.value))}
                        errorMessage={getErrorMessage(errorFields, 'yearManufacture')} />
                    <TextEdit label="Ano do Modelo" type="number" value={yearModel}
                        onChange={e => setYearModel(e.target.value === '' ? '' : Number(e.target.value))}
                        errorMessage={getErrorMessage(errorFields, 'yearModel')} />
                    <TextEdit label="Nº de Série / Chassi" value={serialNumber}
                        onChange={e => setSerialNumber(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'serialNumber')} />
                    <TextEdit label="Código FINAME" value={finameCode}
                        onChange={e => setFinameCode(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'finameCode')} />
                </div>

                <div className="mt-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 px-3 py-2 text-xs text-blue-700 dark:text-blue-300">
                    <span aria-hidden="true" className="font-bold">ℹ</span>
                    <span>
                        Use a <strong>Descrição</strong> para informações sem campo próprio:
                        MDA, número do motor, potência, monitor, itens acoplados e demais
                        observações do bem.
                    </span>
                </div>

                <div className="grid grid-cols-1 gap-3 mt-3">
                    <TextEdit label="Descrição *" value={description}
                        onChange={e => setDescription(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'description')} />
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                    <CurrencyInput label="Valor NF" prefix="R$" fractionDigits={2}
                        value={invoiceValue === '' ? 0 : invoiceValue}
                        onChange={(v) => setInvoiceValue(v)} />
                </div>
            </section>

            {/* Guarda */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Guarda</h4>
                <div className="grid grid-cols-3 gap-3">
                    <TextEdit label="Endereço" value={custodyAddress}
                        onChange={e => setCustodyAddress(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'custodyAddress')} />
                    <TextEdit label="Cidade" value={custodyCity}
                        onChange={e => setCustodyCity(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'custodyCity')} />
                    <SelectTextField label="UF" value={custodyState}
                        onChange={e => setCustodyState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'custodyState')} />
                </div>
            </section>

            {/* Cartórios */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Cartórios</h4>
                <div className="grid grid-cols-1 gap-3">
                    <TextEdit label="Cartório RTD" value={rtdRegistryOffice}
                        onChange={e => setRtdRegistryOffice(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'rtdRegistryOffice')} />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="Cartório RI" value={registryOffice} helperText="opcional"
                        onChange={e => setRegistryOffice(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registryOffice')} />
                    <SelectTextField label="UF Cartório RI" value={registryState}
                        onChange={e => setRegistryState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'registryState')} />
                    <TextEdit label="Matrícula" value={registrationNumber} helperText="opcional"
                        onChange={e => setRegistrationNumber(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'registrationNumber')} />
                </div>
            </section>

            {/* Proprietários (múltiplos) */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Proprietários</h4>
                    <span className="text-xs text-gray-400">{owners.length} / {MAX_OWNERS}</span>
                </div>
                <div className="relative">
                    <TextEdit label="Nome / Razão Social (Enter para buscar)" value={ownerSearch}
                        onChange={e => setOwnerSearch(e.target.value)}
                        onKeyDown={(e: any) => e.key === 'Enter' && searchOwner(ownerSearch)}
                        errorMessage={getErrorMessage(errorFields, 'ownerName')} />
                    {isSearchingOwner && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                    {showOwnerDropdown && ownerResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {ownerResults.slice(0, 8).map((p: any, idx: number) => (
                                <button type="button" key={p.personId || idx} onClick={() => handleSelectOwner(p)}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                    <span className="font-medium dark:text-white">{p.name}</span>
                                    {p.documentFormatted && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{p.documentFormatted}</span>}
                                    <span className="text-[10px] text-gray-400 ml-1">({p.type})</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {owners.length > 0 && (
                    <div className="space-y-1 mt-3">
                        {owners.map((o, i) => (
                            <div key={o.personId || i} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{o.name}</span>
                                    {o.document && <span className="text-gray-500 dark:text-gray-400">{o.document}</span>}
                                </div>
                                <button type="button" onClick={() => removeOwner(i)}
                                    className="text-red-500 hover:text-red-700 text-xs font-medium">Remover</button>
                            </div>
                        ))}
                    </div>
                )}

                {getErrorMessage(errorFields, 'owners') && owners.length === 0 && (
                    <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                        {getErrorMessage(errorFields, 'owners')}
                    </div>
                )}
            </section>

            {/* Custos / Registros (opcional) */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Custos / Registros (opcional)</h4>
                    {!showCostForm && (
                        <button type="button" onClick={() => setShowCostForm(true)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Adicionar</button>
                    )}
                </div>

                {costs.length > 0 && (
                    <div className="space-y-1 mb-3">
                        {costs.map((c, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{COST_TYPE_OPTIONS.find(o => o.value === c.costType)?.label || c.costType}</span>
                                    <span className="text-gray-500">R$ {c.value.toFixed(2)}</span>
                                    <span className="text-gray-400">{c.costDate}</span>
                                    {c.registrationNumber && <span className="text-gray-400">Nº {c.registrationNumber}</span>}
                                </div>
                                <button type="button" onClick={() => removeCost(i)} className="text-red-500 hover:text-red-700 text-xs font-medium">Remover</button>
                            </div>
                        ))}
                    </div>
                )}

                {showCostForm && (
                    <div className="p-3 border border-gray-200 dark:border-slate-700 rounded-lg space-y-3 bg-gray-50 dark:bg-slate-800/50">
                        <div className="grid grid-cols-3 gap-3">
                            <SelectTextField label="Tipo *" value={costType}
                                onChange={e => setCostType(e.target.value)} items={COST_TYPE_OPTIONS} emptyLabel="Selecione" />
                            <CurrencyInput label="Valor *" prefix="R$" fractionDigits={2} value={costValue}
                                onChange={v => setCostValue(v)} />
                            <TextEdit label="Data *" type="date" value={costDate}
                                onChange={e => setCostDate(e.target.value)} />
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            <TextEdit label="Nº Registro" value={costRegNumber}
                                onChange={e => setCostRegNumber(e.target.value)} />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button type="button" onClick={() => setShowCostForm(false)}
                                className="px-3 py-1.5 text-sm text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">Cancelar</button>
                            <button type="button" onClick={handleAddCost}
                                className="px-3 py-1.5 text-sm text-white bg-gray-600 rounded-lg hover:bg-gray-700">Adicionar</button>
                        </div>
                    </div>
                )}
            </section>

            {/* Comentários */}
            <section>
                <TextEdit label="Comentários" value={comments}
                    onChange={e => setComments(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'comments')} />
            </section>
        </div>
    );
};

export default MachineryForm;
